/**
 * VideoAutomationModal — super-admin settings for a branch:
 *  1. Synthesia account link (per-branch API key, template, default avatar, test mode)
 *  2. Social Pro script gating (global): which WMS product/package names count
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { X, Clapperboard, KeyRound, Plug, CheckCircle2, AlertTriangle, Loader2, Trash2, Lock, Plus } from "lucide-react";
import { toast } from "sonner";
import { useStockAvatars } from "./AvatarPicker";
import "./VideoAutomationModal.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VideoAutomationModal({ branch, onClose, onSaved }) {
  const { avatars } = useStockAvatars();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [syn, setSyn] = useState({ has_key: false, key_hint: "", template_id: "", default_avatar_id: "", voice_id: "", enabled: true, test_mode: false });
  const [apiKey, setApiKey] = useState("");
  const [sp, setSp] = useState({ gating_enabled: false, product_patterns: ["social pro"] });
  const [newPattern, setNewPattern] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [s, g] = await Promise.all([
          axios.get(`${API}/admin/branches/${branch.id}/synthesia`),
          axios.get(`${API}/social-pro/settings`),
        ]);
        setSyn(s.data);
        setSp(g.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't load video automation settings");
      } finally {
        setLoading(false);
      }
    })();
  }, [branch.id]);

  const saveSynthesia = async (extra = {}) => {
    setSaving(true);
    try {
      const body = {
        template_id: syn.template_id || "",
        default_avatar_id: syn.default_avatar_id || "",
        voice_id: syn.voice_id || "",
        enabled: syn.enabled !== false,
        test_mode: !!syn.test_mode,
        ...extra,
      };
      if (apiKey.trim()) body.api_key = apiKey.trim();
      const r = await axios.put(`${API}/admin/branches/${branch.id}/synthesia`, body);
      setSyn(r.data);
      setApiKey("");
      toast.success("Synthesia settings saved");
      onSaved && onSaved(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const clearKey = async () => {
    if (!window.confirm("Remove the Synthesia API key for this branch?")) return;
    await saveSynthesia({ clear_api_key: true });
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      if (apiKey.trim()) await saveSynthesia();
      const r = await axios.post(`${API}/admin/branches/${branch.id}/synthesia/test`);
      setTestResult(r.data);
      r.data.ok ? toast.success(r.data.message) : toast.error(r.data.message);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setTestResult({ ok: false, message: typeof d === "string" ? d : "Test failed" });
    } finally {
      setTesting(false);
    }
  };

  const saveGating = async (next) => {
    try {
      const r = await axios.put(`${API}/social-pro/settings`, next);
      setSp(r.data);
      toast.success(r.data.gating_enabled ? "Scripts now limited to Social Pro orders" : "Social Pro gating switched off");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update gating");
    }
  };

  const addPattern = () => {
    const p = newPattern.trim();
    if (!p) return;
    if (sp.product_patterns.some((x) => x.toLowerCase() === p.toLowerCase())) return setNewPattern("");
    saveGating({ ...sp, product_patterns: [...sp.product_patterns, p] });
    setNewPattern("");
  };

  return createPortal(
    <div className="vam-overlay" onClick={onClose} data-testid="video-automation-modal">
      <div className="vam-modal" onClick={(e) => e.stopPropagation()}>
        <div className="vam-head">
          <div className="vam-head-icon"><Clapperboard size={18} /></div>
          <div className="vam-head-text">
            <h3>Video Automation</h3>
            <p>{branch.name}</p>
          </div>
          <button className="vam-close" onClick={onClose} data-testid="vam-close"><X size={18} /></button>
        </div>

        {loading ? (
          <div className="vam-loading"><Loader2 className="animate-spin" size={18} /> Loading…</div>
        ) : (
          <div className="vam-body">
            {/* ── Synthesia ── */}
            <section className="vam-section">
              <div className="vam-section-head">
                <KeyRound size={15} />
                <h4>Synthesia account</h4>
                <span className={`vam-pill ${syn.has_key ? "on" : "off"}`} data-testid="vam-synthesia-pill">
                  {syn.has_key ? `Linked ${syn.key_hint}` : "Not linked"}
                </span>
              </div>
              <p className="vam-help">Each branch uses its own Synthesia workspace. Paste the branch's API key — it's encrypted and never shown again.</p>

              <label className="vam-label">API key</label>
              <div className="vam-key-row">
                <input
                  type="password"
                  className="vam-input"
                  placeholder={syn.has_key ? `Saved ${syn.key_hint} — paste a new key to replace` : "Paste Synthesia API key"}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  autoComplete="off"
                  data-testid="vam-api-key"
                />
                {syn.has_key && (
                  <button className="vam-icon-btn danger" onClick={clearKey} title="Remove key" data-testid="vam-clear-key"><Trash2 size={15} /></button>
                )}
              </div>

              <div className="vam-grid">
                <div>
                  <label className="vam-label">Template ID <span>(optional)</span></label>
                  <input className="vam-input" value={syn.template_id || ""} onChange={(e) => setSyn({ ...syn, template_id: e.target.value })} placeholder="Synthesia template with address / price / sign_off variables" data-testid="vam-template-id" />
                </div>
                <div>
                  <label className="vam-label">Default avatar <span>(if agent doesn't pick)</span></label>
                  <select className="vam-input" value={syn.default_avatar_id || ""} onChange={(e) => setSyn({ ...syn, default_avatar_id: e.target.value })} data-testid="vam-default-avatar">
                    <option value="">— Use Synthesia default —</option>
                    {avatars.map((a) => <option key={a.id} value={a.synthesia_avatar_id}>{a.name} — {a.tagline}</option>)}
                  </select>
                </div>
                <div>
                  <label className="vam-label">Voice ID <span>(optional)</span></label>
                  <input className="vam-input" value={syn.voice_id || ""} onChange={(e) => setSyn({ ...syn, voice_id: e.target.value })} placeholder="Synthesia voice UUID" data-testid="vam-voice-id" />
                </div>
                <div className="vam-toggles">
                  <label className="vam-check">
                    <input type="checkbox" checked={syn.enabled !== false} onChange={(e) => setSyn({ ...syn, enabled: e.target.checked })} data-testid="vam-enabled" />
                    <span>Enabled for this branch</span>
                  </label>
                  <label className="vam-check">
                    <input type="checkbox" checked={!!syn.test_mode} onChange={(e) => setSyn({ ...syn, test_mode: e.target.checked })} data-testid="vam-test-mode" />
                    <span>Test mode (watermarked, no credits)</span>
                  </label>
                </div>
              </div>

              {testResult && (
                <div className={`vam-test ${testResult.ok ? "ok" : "bad"}`} data-testid="vam-test-result">
                  {testResult.ok ? <CheckCircle2 size={15} /> : <AlertTriangle size={15} />}
                  <div>
                    <strong>{testResult.message}</strong>
                    {testResult.templates?.length > 0 && (
                      <ul>
                        {testResult.templates.map((t) => (
                          <li key={t.id}>
                            <button type="button" className="vam-link" onClick={() => setSyn({ ...syn, template_id: t.id })} title="Use this template">{t.title}</button>
                            <code>{t.id}</code>
                            {t.variables?.length > 0 && <span className="vam-vars">vars: {t.variables.join(", ")}</span>}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}

              <div className="vam-actions">
                <button className="vam-btn ghost" onClick={testConnection} disabled={testing || (!syn.has_key && !apiKey.trim())} data-testid="vam-test-btn">
                  {testing ? <Loader2 className="animate-spin" size={15} /> : <Plug size={15} />} Test connection
                </button>
                <button className="vam-btn primary" onClick={() => saveSynthesia()} disabled={saving} data-testid="vam-save-btn">
                  {saving ? <Loader2 className="animate-spin" size={15} /> : <CheckCircle2 size={15} />} Save Synthesia settings
                </button>
              </div>
            </section>

            {/* ── Social Pro gating ── */}
            <section className="vam-section">
              <div className="vam-section-head">
                <Lock size={15} />
                <h4>Social Pro script gating</h4>
                <span className="vam-global">applies to all branches</span>
                <button
                  type="button"
                  className={`vam-switch ${sp.gating_enabled ? "on" : ""}`}
                  onClick={() => saveGating({ ...sp, gating_enabled: !sp.gating_enabled })}
                  data-testid="vam-gating-toggle"
                  aria-pressed={sp.gating_enabled}
                >
                  <span className="vam-switch-knob" />
                  <span className="vam-switch-label">{sp.gating_enabled ? "On" : "Off"}</span>
                </button>
              </div>
              <p className="vam-help">
                When on, agents can only add a script to a WMS order whose products or package name contain one of these words. Checked live against WMS on every submission.
              </p>
              <div className="vam-patterns" data-testid="vam-patterns">
                {sp.product_patterns.map((p) => (
                  <span key={p} className="vam-pattern">
                    {p}
                    {sp.product_patterns.length > 1 && (
                      <button type="button" onClick={() => saveGating({ ...sp, product_patterns: sp.product_patterns.filter((x) => x !== p) })} title="Remove" data-testid={`vam-pattern-remove-${p}`}><X size={12} /></button>
                    )}
                  </span>
                ))}
                <div className="vam-pattern-add">
                  <input className="vam-input" value={newPattern} onChange={(e) => setNewPattern(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addPattern()} placeholder="Add product / package name" data-testid="vam-pattern-input" />
                  <button type="button" className="vam-icon-btn" onClick={addPattern} data-testid="vam-pattern-add"><Plus size={15} /></button>
                </div>
              </div>
            </section>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
